import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { APP_TOKEN_KEY } from "./appAuthSlice";
import { toggleFollow, generateInviteCode, setCustomInviteCode } from "./profileSlice";
import { computeTotalScore } from "../utils/dashboardHelpers";
import { isFeedFresh, parseUserIdArg, shouldSkipCachedFeed } from "../../utils/feedFreshness";
import { isNetworkOffline } from "../../utils/requireNetwork";
import { attachGetRetryInterceptor } from "../../utils/axiosRetry";

const dashboardApi = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
  timeout: 25000,
});

attachGetRetryInterceptor(dashboardApi);

dashboardApi.interceptors.request.use((config) => {
  const token = localStorage.getItem(APP_TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const STATUS_QUERY = {
  pending: "Pending",
  success: "Success",
  failed: "Failed",
  cancelled: "Cancelled",
  canceled: "Cancelled",
  hold: "Hold",
  onhold: "Hold",
};

export const fetchDashboardProfile = createAsyncThunk(
  "dashboard/fetchDashboardProfile",
  async (arg, { rejectWithValue }) => {
    const { userId } = parseUserIdArg(arg);
    try {
      const res = await dashboardApi.get(`/api/users/${userId}`);
      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to load profile");
    }
  },
  {
    condition: (arg, { getState }) => {
      const { userId, force } = parseUserIdArg(arg);
      if (!userId) return false;
      if (force) return true;
      if (isNetworkOffline()) return false;
      const state = getState().dashboard;
      if (String(state.profileUserId) !== String(userId)) return true;
      return !isFeedFresh(state.profileLastFetchedAt);
    },
  }
);

export const fetchDashboardScores = createAsyncThunk(
  "dashboard/fetchDashboardScores",
  async (arg = {}, { rejectWithValue }) => {
    const force = arg?.force ?? false;
    try {
      const res = await dashboardApi.get("/api/users/scores");
      const rows = Array.isArray(res.data) ? res.data : [];
      return rows
        .map((row) => ({
          ...row,
          computedTotal: row.computedTotal ?? computeTotalScore(row),
        }))
        .sort((a, b) => (b.computedTotal || 0) - (a.computedTotal || 0));
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to load leaderboard");
    }
  },
  {
    condition: (arg = {}, { getState }) => {
      const force = arg?.force ?? false;
      if (force) return true;
      if (isNetworkOffline()) return false;
      const state = getState().dashboard;
      return !shouldSkipCachedFeed({
        force,
        fetchedAt: state.scoresLastFetchedAt,
        hasData: state.scoresLastFetchedAt != null,
      });
    },
  }
);

export const fetchDashboardTransactions = createAsyncThunk(
  "dashboard/fetchDashboardTransactions",
  async (
    { userId, type = "all", status = "all", page = 1, limit = 20, append = false, force = false },
    { rejectWithValue }
  ) => {
    try {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("limit", String(limit));
      if (type && type !== "all") params.set("type", type);
      const apiStatus = STATUS_QUERY[String(status).toLowerCase()];
      if (apiStatus) params.set("status", apiStatus);

      const res = await dashboardApi.get(`/api/transactions/user/${userId}?${params.toString()}`);
      return {
        transactions: res.data?.transactions || [],
        pagination: res.data?.pagination || {},
        append,
        type,
        status,
        page,
        limit,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to load transactions");
    }
  },
  {
    condition: (
      { page = 1, append = false, force = false, type = "all", status = "all" },
      { getState }
    ) => {
      if (force) return true;
      if (isNetworkOffline()) return false;
      if (append || page > 1) return true;
      const state = getState().dashboard;
      const sameFilter =
        state.txFilter.type === type && state.txFilter.status === status;
      if (!sameFilter) return true;
      return state.transactionsLastFetchedAt == null || !isFeedFresh(state.transactionsLastFetchedAt);
    },
  }
);

export const fetchDashboardTxAnalytics = createAsyncThunk(
  "dashboard/fetchDashboardTxAnalytics",
  async (arg, { rejectWithValue }) => {
    const { userId } = parseUserIdArg(arg);
    const limit =
      typeof arg === "object" && arg?.limit != null ? arg.limit : 250;
    try {
      const res = await dashboardApi.get(
        `/api/transactions/user/${userId}?page=1&limit=${limit}`
      );
      return {
        transactions: res.data?.transactions || [],
        total: res.data?.pagination?.totalTransactions || 0,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to load analytics");
    }
  },
  {
    condition: (arg, { getState }) => {
      const { userId, force } = parseUserIdArg(arg);
      if (!userId) return false;
      if (force) return true;
      if (isNetworkOffline()) return false;
      const state = getState().dashboard;
      if (String(state.analyticsUserId) !== String(userId)) return true;
      return !isFeedFresh(state.analyticsLastFetchedAt);
    },
  }
);

const initialState = {
  profile: null,
  profileStatus: "idle",
  profileError: null,
  profileLastFetchedAt: null,
  profileUserId: null,
  analyticsTx: [],
  analyticsTxTotal: 0,
  analyticsTxStatus: "idle",
  analyticsLastFetchedAt: null,
  analyticsUserId: null,
  scores: [],
  scoresStatus: "idle",
  scoresError: null,
  scoresLastFetchedAt: null,
  transactions: [],
  transactionsStatus: "idle",
  transactionsError: null,
  transactionsLastFetchedAt: null,
  txPagination: { currentPage: 1, hasNext: false, totalTransactions: 0 },
  txFilter: { type: "all", status: "all" },
  loadingMoreTx: false,
};

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    setDashboardTxFilter: (state, action) => {
      state.txFilter = { ...state.txFilter, ...action.payload };
      state.transactions = [];
      state.transactionsStatus = "loading";
      state.transactionsError = null;
      state.transactionsLastFetchedAt = null;
      state.txPagination = { currentPage: 1, hasNext: false, totalTransactions: 0 };
    },
    applyDashboardWalletBalances: (state, action) => {
      if (!state.profile) return;
      const { CASH, COIN } = action.payload || {};
      if (CASH != null) state.profile.CASH = CASH;
      if (COIN != null) state.profile.COIN = COIN;
    },
    clearDashboard: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDashboardProfile.pending, (state) => {
        state.profileStatus = "loading";
        state.profileError = null;
      })
      .addCase(fetchDashboardProfile.fulfilled, (state, action) => {
        state.profileStatus = "succeeded";
        state.profile = action.payload;
        state.profileLastFetchedAt = Date.now();
        const { userId } = parseUserIdArg(action.meta.arg);
        if (userId) state.profileUserId = userId;
      })
      .addCase(fetchDashboardProfile.rejected, (state, action) => {
        state.profileStatus = "failed";
        state.profileError = action.payload;
      })

      .addCase(fetchDashboardScores.pending, (state) => {
        state.scoresStatus = "loading";
        state.scoresError = null;
      })
      .addCase(fetchDashboardScores.fulfilled, (state, action) => {
        state.scoresStatus = "succeeded";
        state.scores = action.payload;
        state.scoresLastFetchedAt = Date.now();
      })
      .addCase(fetchDashboardScores.rejected, (state, action) => {
        state.scoresStatus = "failed";
        state.scoresError = action.payload;
      })

      .addCase(fetchDashboardTransactions.pending, (state, action) => {
        if (action.meta.arg?.append) state.loadingMoreTx = true;
        else state.transactionsStatus = "loading";
        state.transactionsError = null;
      })
      .addCase(fetchDashboardTransactions.fulfilled, (state, action) => {
        state.transactionsStatus = "succeeded";
        state.loadingMoreTx = false;
        const { transactions, pagination, append } = action.payload;
        state.transactions = append ? [...state.transactions, ...transactions] : transactions;
        state.txPagination = {
          currentPage: pagination?.currentPage || 1,
          hasNext: !!pagination?.hasNext,
          totalTransactions: pagination?.totalTransactions || state.transactions.length,
        };
        if (!append && (action.meta.arg?.page ?? 1) === 1) {
          state.transactionsLastFetchedAt = Date.now();
        }
      })
      .addCase(fetchDashboardTransactions.rejected, (state, action) => {
        state.transactionsStatus = "failed";
        state.loadingMoreTx = false;
        state.transactionsError = action.payload;
      })

      .addCase(fetchDashboardTxAnalytics.pending, (state) => {
        state.analyticsTxStatus = "loading";
      })
      .addCase(fetchDashboardTxAnalytics.fulfilled, (state, action) => {
        state.analyticsTxStatus = "succeeded";
        state.analyticsTx = action.payload.transactions;
        state.analyticsTxTotal = action.payload.total;
        state.analyticsLastFetchedAt = Date.now();
        const { userId } = parseUserIdArg(action.meta.arg);
        if (userId) state.analyticsUserId = userId;
      })
      .addCase(fetchDashboardTxAnalytics.rejected, (state) => {
        state.analyticsTxStatus = "failed";
      })

      .addCase(toggleFollow.fulfilled, (state, action) => {
        const { followed, currentUserId, targetId } = action.payload;
        if (!state.profile) return;

        if (String(state.profile._id) === String(targetId)) {
          const followers = Array.isArray(state.profile.followers)
            ? [...state.profile.followers]
            : [];
          if (followed) {
            if (!followers.some((id) => String(id) === String(currentUserId))) {
              followers.push(currentUserId);
            }
            state.profile.followers = followers;
          } else {
            state.profile.followers = followers.filter(
              (id) => String(id) !== String(currentUserId)
            );
          }
        }

        if (String(state.profile._id) === String(currentUserId)) {
          const following = Array.isArray(state.profile.following)
            ? [...state.profile.following]
            : [];
          if (followed) {
            if (!following.some((id) => String(id) === String(targetId))) {
              following.push(targetId);
            }
            state.profile.following = following;
          } else {
            state.profile.following = following.filter(
              (id) => String(id) !== String(targetId)
            );
          }
        }
      })
      .addCase(generateInviteCode.fulfilled, (state, action) => {
        if (state.profile && action.payload?.inviteCode) {
          state.profile.inviteCode = action.payload.inviteCode;
        }
      })
      .addCase(setCustomInviteCode.fulfilled, (state, action) => {
        if (state.profile && action.payload?.inviteCode) {
          state.profile.inviteCode = action.payload.inviteCode;
        }
      });
  },
});

export const { setDashboardTxFilter, applyDashboardWalletBalances, clearDashboard } = dashboardSlice.actions;
export default dashboardSlice.reducer;
